// TreatmentProcedureStep.jsx - Parent Component
import React, { useContext, useEffect, useState } from "react";
import { Card, Button, Container } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import "../MoComponent.css";
import Header from "../../Header/Header";
import PatientCard from "../../PatientCard/PatientCard";
import { useLocation, useNavigate } from "react-router-dom";
import { CommonContext } from "../../../context/CommonContext";
import { apisPath, paths } from "../../../utils/path";
import OtpVerificationModal from "../OtpVerification/OtpVerification";
import PreChemoCheckup from "./PreChemoCheckup/PreChemoCheckup";
import ChemoAdministration from "./ChemoAdministration/ChemoAdministration";
import { authHttp } from "../../../services/commonHttpService";
import { formatDate, shouldAllowChemoAdmin } from "../../../helpers/commonHelper";

const steps = [
    {
        title: "Pre Chemotherapy Check Up",
        description: "Lab results & patient checkup date",
        component: PreChemoCheckup,
        workflowId: 9,
    },
    {
        title: "Chemotherapy Administration Details",
        description: "Details of administration chemotherapy",
        component: ChemoAdministration,
        workflowId: 11,
    },
];

const TreatmentProcedureStep = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { setAlertState, setIsLoading } = useContext(CommonContext);

    const [currentStep, setCurrentStep] = useState(0);
    const [openOtpModal, setOpenOtpModal] = useState(false);
    const [completedSteps, setCompletedSteps] = useState([]);
    const [submitForm, setSubmitForm] = useState(false);
    const [patientData, setPatientData] = useState(null);
    const [isRedirected, setIsRedirected] = useState(false);
    const [chemoSessionDate, setChemoSessionDate] = useState(null);

    const patientId = location.state?.patientId;
    const planned_chemo_cycles = location.state?.planned_chemo_cycles;
    const upcoming_on_min = location.state?.upcoming_on_min;
    const step5Data = location.state?.step5Data;

    const StepComponent = steps[currentStep].component;

    // Fetch patient details to get workflow_id
    useEffect(() => {
        if (patientId) {
            fetchPatientDetails();
        } else {
            setAlertState({
                open: true,
                title: "Patient ID not found. Please select a patient from the beneficiary list.",
                btnTxt: "GO BACK",
                status: "fail",
            });
            navigate(`${paths?.user?.commonPoint}${paths?.user?.beneficiaryManagement}`);
        }
    }, [patientId]);

    const fetchPatientDetails = async () => {
        setIsLoading(true);
        try {
            const response = await authHttp.get(`${apisPath?.patientDetails}/${patientId}`);

            if (response?.data?.status || response?.data?.data) {
                const data = response?.data?.data;
                setPatientData(data);

                // Extract chemo_session_date from API response
                const sessionDate = data?.chemo_session_date;
                setChemoSessionDate(sessionDate);

                // Determine initial step based on workflow_id
                const workflowId = data?.workflow_id;

                if (workflowId === 9) {
                    // Direct to PreChemoCheckup (step 0)
                    setCurrentStep(0);
                    setIsRedirected(true);
                } else if (workflowId === 11) {
                    // Show OTP modal first, then ChemoAdministration
                    setCurrentStep(1);
                    setIsRedirected(true);
                    setCompletedSteps([0]); // Mark step 0 as completed

                    // Check if session date allows chemo admin
                    const isAllowed = shouldAllowChemoAdmin(sessionDate);

                    if (isAllowed) {
                        // Show OTP modal
                        setOpenOtpModal(true);
                    } else {
                        // Date doesn't allow, show alert and redirect
                        setAlertState({
                            open: true,
                            title: `You can only submit ChemoAdministration data after the treatment date or the day of treatment ie. ${formatDate(upcoming_on_min)}`,
                            btnTxt: "CLOSE",
                            status: "fail",
                            onClose: () => {
                                navigate(`${paths?.user?.commonPoint}${paths?.user?.beneficiaryManagement}`);
                            }
                        });
                    }
                } else {
                    // Default to first step if workflow_id is different
                    setCurrentStep(0);
                    setIsRedirected(true);
                }
            }
        } catch (error) {
            setAlertState({
                open: true,
                title: error?.response?.data?.message || "Failed to fetch patient details",
                btnTxt: "CLOSE",
                status: "fail",
            });
        } finally {
            setIsLoading(false);
        }
    };

    // When OTP verified successfully
    const handleOtpVerified = () => {
        // Just close OTP modal and proceed to ChemoAdministration
        setOpenOtpModal(false);
    };

    // Child callback result - called after form submission
    const onFormResult = (success) => {
        if (success) {
            // For step 0, mark as completed and move to step 1, then open OTP modal
            if (currentStep === 0) {
                setCompletedSteps((prev) => [...prev, 0]); // Mark step 0 as completed
                setCurrentStep(1); // Move to ChemoAdministration
                setOpenOtpModal(true); // Show OTP modal
            } else {
                // For other steps, directly advance
                setCompletedSteps((prev) => [...prev, currentStep]);

                if (currentStep < steps.length - 1) {
                    setCurrentStep(currentStep + 1);
                } else {
                    setAlertState({
                        open: true,
                        title: "Treatment procedure completed successfully!",
                        btnTxt: "CLOSE",
                        status: "success",
                    });
                }
            }
        }

        setSubmitForm(false); // Reset after each submit
    };

    // Handle next button - always trigger form submission first
    const handleNext = () => {
        setSubmitForm(true);
    };

    // Handle previous button
    const handlePrevious = () => {
        setCurrentStep(currentStep - 1);
        setIsRedirected(false); // User navigated manually, not redirected
    };

    return (
        <>
            <Header showFullHeader={false} />
            <Container>
                <div className="my-4 tp-container">
                    {/* ---------------- TITLE ---------------- */}
                    <h2 className="mb-4">Treatment & Procedure Module</h2>

                    <div className="row">
                        {/* ---------------- Patient Card ---------------- */}
                        <div className="col-md-3 col-12 mb-3">
                            <PatientCard patientId={patientId} patientData={patientData} />
                        </div>

                        {/* ---------------- Step Cards ---------------- */}
                        <div className="col-md-9 col-12">
                            <h5 className="sub-header">Treatment Workflow Process</h5>
                            <div className="row g-3">
                                {steps.map((step, index) => (
                                    <div className="col-md-3 col-12" key={index}>
                                        <Card
                                            className={`workflow-card p-3 shadow-sm ${completedSteps.includes(index) ? "completed-step" : ""
                                                } ${currentStep === index ? "active-step" : ""}`}
                                        >
                                            <strong className="header-tiles">{step?.title}</strong>
                                            <p className="font-para-dis mb-0">{step?.description}</p>
                                        </Card>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* ---------------- Step Component ---------------- */}
                    <OtpVerificationModal
                        open={openOtpModal}
                        onClose={() => setOpenOtpModal(false)}
                        onVerified={handleOtpVerified}
                    />

                    <Card className="shadow-sm mt-4">
                        <div className="card-body">
                            <StepComponent
                                patientId={patientId}
                                submitForm={submitForm}
                                formResult={onFormResult}
                                currentStep={currentStep}
                                isRedirected={isRedirected}
                                planned_chemo_cycles={planned_chemo_cycles}
                                chemoSessionDate={chemoSessionDate}
                                upcomingOnMin={upcoming_on_min}
                                step5Data={step5Data}
                            />

                            {/* Buttons */}
                            <div className="d-flex justify-content-end mt-4 gap-2">
                                {currentStep > 0 && (
                                    <Button
                                        variant="outlined"
                                        startIcon={<ArrowBackIcon />}
                                        sx={{ borderRadius: "20px", px: 3 }}
                                        onClick={handlePrevious}
                                        className="next-btn-main"
                                    >
                                        Previous
                                    </Button>
                                )}

                                {currentStep < steps.length - 1 ? (
                                    <Button
                                        variant="contained"
                                        sx={{ borderRadius: "20px", px: 3 }}
                                        onClick={handleNext}
                                        className="next-btn-main"
                                    >
                                        Next →
                                    </Button>
                                ) : (
                                    <Button
                                        variant="contained"
                                        sx={{ borderRadius: "20px", px: 3 }}
                                        onClick={handleNext}
                                        className="next-btn-main"
                                    >
                                        Submit
                                    </Button>
                                )}
                            </div>
                        </div>
                    </Card>
                </div>
            </Container>
        </>
    );
};

export default TreatmentProcedureStep;